<template>
    <v-container fluid>
        <dynamic-toolbar
            :icon="'mdi-gas-station'"
            :title="'Readings V2 (BETA)'"
            :color="moduleColor"
            :theme="'dark'"
            :loading="loading"
        >
            <template v-slot:buttons>
                <v-btn
                    color="primary"
                    @click="showReportHeader = !showReportHeader"
                >
                    <v-icon>mdi-filter</v-icon>
                    <span>Filters</span>
                </v-btn>
            </template>
        </dynamic-toolbar>
        <v-card v-if="showReportHeader" class="mb-4" tile>
            <v-card-title>Filters</v-card-title>
            <v-card-text>
                <v-form ref="form" v-model="valid" lazy-validation>
                    <v-row justify="center">
                        <v-col cols="2">
                            <v-select
                                v-model="region"
                                :items="regions"
                                :item-text="'PrincipalRegion'"
                                :item-value="'PrincipalRegionID'"
                                label="Select Region"
                                prepend-icon="mdi-map"
                            ></v-select>
                        </v-col>
                        <v-col cols="4">
                            <v-combobox
                                v-model="search"
                                :items="stations"
                                label="Search"
                                prepend-icon="mdi-magnify"
                            ></v-combobox>
                        </v-col>
                        <v-col cols="2" align-self="center">
                            <v-btn
                                color="secondary"
                                class="my-auto"
                                @click="getReadings"
                                >Get Readings</v-btn
                            >
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>
        </v-card>
        <v-data-table
            :headers="headers"
            :items="readings"
            :search="search"
            class="elevation-10"
            :items-per-page="15"
            :options.sync="options"
            :server-items-length="totalReadingStations"
            :loading="dataLoading"
            single-expand
            show-expand
            fixed-header
            height="70vh"
            dense
            expand-icon="mdi-arrow-down-drop-circle"
            :expanded.sync="expanded"
            multi-sort
            :sort-by="['closest_runout']"
            :sort-desc="[false]"
            @update:options="getReadings"
        >
            <template v-slot:item.StationNumber="{ item }">
                <v-btn
                    dense
                    block
                    :color="moduleColor"
                    dark
                    @click="openSitemap(item)"
                >
                    {{ item.StationNumber }}
                </v-btn>
            </template>
            <template v-slot:item.actions="{ item }">
                <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            color="success"
                            icon
                            v-bind="attrs"
                            v-on="on"
                            @click="openStationChart(item)"
                        >
                            <v-icon>mdi-chart-line</v-icon>
                        </v-btn>
                    </template>
                    <span>View Station Chart</span>
                </v-tooltip>
                <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            color="success"
                            icon
                            v-bind="attrs"
                            v-on="on"
                            @click="createOrder(item)"
                            readonly
                        >
                            <v-icon>mdi-invoice-text-plus</v-icon>
                        </v-btn>
                    </template>
                    <span>Create Order</span>
                </v-tooltip>
            </template>

            <template v-slot:item.current_order="{ item }">
                <span class="text-caption">
                    <v-icon small>mdi-progress-wrench</v-icon>
                    In development
                </span>
            </template>

            <template v-slot:item.closest_runout="{ item }">
                <v-chip
                    label
                    :color="getRunoutColor(item.closest_runout)"
                    :text-color="
                        getRunoutColor(item.closest_runout) ===
                        'rgb(255, 255, 224)'
                            ? 'black'
                            : 'white'
                    "
                >
                    <v-icon
                        v-if="
                            moment(item.closest_runout).diff(
                                moment(),
                                'hours'
                            ) <= 12
                        "
                        left
                        small
                    >
                        mdi-alert
                    </v-icon>
                    {{ runoutCountdown(item.closest_runout) }}
                </v-chip>
            </template>

            <template v-slot:header.tank-data="{ header }">
                <div class="d-flex align-center">
                    {{ header.text }}
                    <v-btn small text @click="toggleGrouping" class="ml-2">
                        {{
                            groupBy === 'tanks'
                                ? 'Group by Products'
                                : 'Group by Tanks'
                        }}
                    </v-btn>
                </div>
            </template>

            <template v-slot:item.tank-data="{ item }">
                <div
                    v-for="(tankOrProduct, index) in getGroupedData(item)"
                    :key="index"
                    class="custom-chip mr-2 my-2"
                >
                    <v-tooltip bottom :color="moduleColor">
                        <template v-slot:activator="{ on, attrs }">
                            <div
                                class="chip-fill"
                                :style="{
                                    width:
                                        calculateFillPercentage(
                                            item,
                                            tankOrProduct
                                        ) + '%',
                                    backgroundColor: calculateRunoutColor(
                                        item,
                                        tankOrProduct
                                    )
                                }"
                            ></div>
                            <span
                                v-on="on"
                                v-bind="attrs"
                                class="chip-text"
                                style="cursor: help"
                            >
                                {{
                                    getProductName(
                                        tankOrProduct.Product ||
                                            tankOrProduct.product_id
                                    )
                                }}
                                <span class="text-caption text--secondary">
                                    {{
                                        getPercentage(item, tankOrProduct) + '%'
                                    }}
                                </span>
                            </span>
                        </template>
                        <v-list-item dark>
                            <v-list-item-content>
                                <v-list-item-title>
                                    <v-icon>mdi-gas-station</v-icon>
                                    {{
                                        getProductName(
                                            tankOrProduct.Product ||
                                                tankOrProduct.product_id
                                        )
                                    }}
                                </v-list-item-title>
                                <v-list-item-subtitle>
                                    Total Volume:
                                    {{ getTotalVolume(item, tankOrProduct) }}
                                </v-list-item-subtitle>
                                <v-list-item-subtitle>
                                    Safe Fill:
                                    {{ getTotalSafeFill(item, tankOrProduct) }}
                                </v-list-item-subtitle>
                                <v-list-item-subtitle>
                                    Safe Pump:
                                    {{ getTotalSafePump(item, tankOrProduct) }}
                                </v-list-item-subtitle>
                                <template v-if="groupBy === 'products'">
                                    <v-list-item-subtitle
                                        v-for="tank in getProductTanks(
                                            item,
                                            tankOrProduct.product_id
                                        )"
                                        :key="tank.ID"
                                    >
                                        Tank {{ tank.Tank }}:
                                        {{ tank.volume }} / {{ tank.SafeFill }}
                                    </v-list-item-subtitle>
                                </template>
                                <v-list-item-subtitle>
                                    Estimated Runout:
                                    <template v-if="groupBy === 'tanks'">
                                        {{
                                            runoutCountdown(
                                                tankOrProduct.safe_runout_at
                                            )
                                        }}
                                    </template>
                                    <template v-else>
                                        {{
                                            runoutCountdown(
                                                tankOrProduct.safe_runout_at
                                            )
                                        }}
                                    </template>
                                </v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                    </v-tooltip>
                </div>
            </template>
            <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length">
                    <v-data-iterator
                        :items="getGroupedData(item)"
                        hide-default-footer
                    >
                        <template v-slot:default="props">
                            <v-row no-gutters>
                                <v-col
                                    v-for="(tankOrProduct,
                                    index) in props.items"
                                    :key="index"
                                >
                                    <v-card class="mx-2 mt-2">
                                        <v-card-title>
                                            <v-icon left
                                                >mdi-gas-station</v-icon
                                            >
                                            <template
                                                v-if="groupBy === 'tanks'"
                                            >
                                                <v-icon>
                                                    mdi-numeric-{{
                                                        tankOrProduct.Tank
                                                    }}
                                                </v-icon>
                                            </template>
                                            <template v-else>
                                                <v-icon
                                                    v-for="tank in getProductTanks(
                                                        item,
                                                        tankOrProduct.product_id
                                                    )"
                                                    :key="tank.Tank"
                                                >
                                                    mdi-numeric-{{ tank.Tank }}
                                                </v-icon>
                                            </template>
                                            {{
                                                getProductName(
                                                    tankOrProduct.Product ||
                                                        tankOrProduct.product_id
                                                )
                                            }}
                                        </v-card-title>
                                        <v-card-subtitle
                                            v-if="groupBy === 'tanks'"
                                        >
                                            Updated:
                                            {{
                                                tankOrProduct.volume_updated_at
                                            }}
                                            {{
                                                timeSince(
                                                    tankOrProduct.volume_updated_at
                                                )
                                            }}
                                        </v-card-subtitle>
                                        <v-divider></v-divider>
                                        <v-progress-linear
                                            :value="
                                                calculateFillPercentage(
                                                    item,
                                                    tankOrProduct
                                                )
                                            "
                                            :color="
                                                calculateRunoutColor(
                                                    item,
                                                    tankOrProduct
                                                )
                                            "
                                        ></v-progress-linear>
                                        <v-list dense>
                                            <v-list-item>
                                                <v-list-item-content>
                                                    Total Volume:
                                                </v-list-item-content>
                                                <v-list-item-content
                                                    class="align-end"
                                                >
                                                    <v-chip
                                                        label
                                                        class="font-weight-bold"
                                                        :style="{
                                                            background: calculateRunoutColor(
                                                                item,
                                                                tankOrProduct
                                                            )
                                                        }"
                                                    >
                                                        {{
                                                            getTotalVolume(
                                                                item,
                                                                tankOrProduct
                                                            )
                                                        }}
                                                        /
                                                        {{
                                                            getTotalSafeFill(
                                                                item,
                                                                tankOrProduct
                                                            )
                                                        }}
                                                        -
                                                        {{
                                                            getPercentage(
                                                                item,
                                                                tankOrProduct
                                                            )
                                                        }}%
                                                    </v-chip>
                                                </v-list-item-content>
                                            </v-list-item>
                                            <template
                                                v-if="groupBy === 'products'"
                                            >
                                                <v-list-item
                                                    v-for="tank in getProductTanks(
                                                        item,
                                                        tankOrProduct.product_id
                                                    )"
                                                    :key="tank.Tank"
                                                >
                                                    <v-list-item-content>
                                                        Tank {{ tank.Tank }}:
                                                    </v-list-item-content>
                                                    <v-list-item-content
                                                        class="align-end"
                                                    >
                                                        {{ tank.volume }} /
                                                        {{ tank.SafeFill }}
                                                    </v-list-item-content>
                                                </v-list-item>
                                            </template>
                                            <v-list-item>
                                                <v-list-item-content>
                                                    Safe Fill (max.):
                                                </v-list-item-content>
                                                <v-list-item-content
                                                    class="align-end green--text"
                                                >
                                                    {{
                                                        getTotalSafeFill(
                                                            item,
                                                            tankOrProduct
                                                        )
                                                    }}
                                                </v-list-item-content>
                                            </v-list-item>
                                            <v-list-item>
                                                <v-list-item-content>
                                                    Safe Pump (min.):
                                                </v-list-item-content>
                                                <v-list-item-content
                                                    class="align-end red--text"
                                                >
                                                    {{
                                                        getTotalSafePump(
                                                            item,
                                                            tankOrProduct
                                                        )
                                                    }}
                                                </v-list-item-content>
                                            </v-list-item>
                                            <v-list-item>
                                                <v-list-item-content>
                                                    Runout:
                                                </v-list-item-content>
                                                <v-list-item-content>
                                                    {{
                                                        tankOrProduct.safe_runout_at
                                                    }}
                                                    <v-btn
                                                        :color="
                                                            calculateRunoutColor(
                                                                item,
                                                                tankOrProduct
                                                            )
                                                        "
                                                        class="mr-1 mb-1"
                                                        small
                                                        @click="
                                                            openTankChart(
                                                                tankOrProduct
                                                            )
                                                        "
                                                    >
                                                        <v-icon left small
                                                            >mdi-alert</v-icon
                                                        >
                                                        <span
                                                            class="text-caption"
                                                        >
                                                            {{
                                                                runoutCountdown(
                                                                    tankOrProduct.safe_runout_at
                                                                )
                                                            }}
                                                        </span>
                                                    </v-btn>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </v-list>
                                    </v-card>
                                </v-col>
                            </v-row>
                        </template>
                    </v-data-iterator>
                    <v-btn plain @click="toggleGrouping" class="mt-2">
                        {{
                            groupBy === 'tanks'
                                ? 'View by Products'
                                : 'View by Tanks'
                        }}
                    </v-btn>
                </td>
            </template>
        </v-data-table>
        <v-dialog v-model="tankChartDialog" max-width="60vw">
            <v-card>
                <v-img
                    contain
                    max-height="80vh"
                    :src="
                        'http://localhost:8000/storage/forecast_plots/forecast_plot_station_' +
                            selectedItem.StationNum +
                            '_tank_' +
                            selectedItem.Tank +
                            '.png'
                    "
                >
                </v-img>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" @click="tankChartDialog = false">
                        Close
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            max-width="1000"
            v-model="SiteDialog"
            transition="dialog-bottom-transition"
            overlay-color="accent"
        >
            <v-toolbar dark color="primary" dense>
                <v-spacer></v-spacer>
                <v-toolbar-title>Site # {{ shiftSelected }}</v-toolbar-title>
                <v-spacer></v-spacer>

                <v-btn icon dark @click="SiteDialog = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-card>
                <SiteReadings :onlyShow="true" />
            </v-card>
        </v-dialog>

        <v-dialog
            max-width="1000"
            v-model="showSitemap"
            transition="dialog-bottom-transition"
            overlay-color="accent"
            persistent
        >
            <v-toolbar dark color="primary" dense>
                <v-spacer></v-spacer>
                <v-toolbar-title
                    >Sitemap {{ currItemSitemap.SiteNum }}</v-toolbar-title
                >
                <v-spacer></v-spacer>
                <v-btn icon dark @click="showSitemap = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-card>
                <iframe
                    :src="currItemSitemap.SitemapLink"
                    style="width: 100%; height:100vh;"
                ></iframe>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="missingDialog"
            transition="dialog-bottom-transition"
            overlay-color="accent"
        >
            <v-toolbar dark color="primary">
                <v-spacer></v-spacer>
                <v-toolbar-title>Missing Readings</v-toolbar-title>
                <v-spacer></v-spacer>

                <v-btn icon dark @click="missingDialog = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-card>
                <v-data-table
                    :headers="headers"
                    :items="missing"
                    :search="search"
                    class="elevation-10"
                    :loading="dataLoading"
                    :items-per-page="13"
                    loading-text="Loading... Please wait"
                >
                </v-data-table>
            </v-card>
        </v-dialog>

        <OrdersForm :siteData="SiteData" v-if="$store.state.order.rdnAdd" />
        <MultiReadings
            :preOrders="preOrders"
            v-if="$store.state.readings.multiple"
        ></MultiReadings>
        <ReadingsChart
            :value="stationChartDialog"
            :station="selectedItem"
            :products="products"
            @input="stationChartDialog = $event"
        ></ReadingsChart>
    </v-container>
</template>

<script>
import axios from 'axios';
import moment from 'moment-timezone';

import OrdersForm from '@/components/Modals/Dispatch/Orders/AddOrderReadings.vue';
import SiteReadings from '@/components/Modals/Dispatch/Readings/SiteReadings.vue';
import MultiReadings from '@/components/Modals/Dispatch/Orders/MultiAddReadings.vue';
import ReadingsChart from '../../components/Dispatch/Readings/ReadingsChart.vue';
import DynamicToolbar from '../../components/Global/Utils/DynamicToolbar.vue';
export default {
    name: 'Readings',
    components: {
        OrdersForm,
        SiteReadings,
        MultiReadings,
        ReadingsChart,
        DynamicToolbar
    },
    data: function() {
        return {
            moduleColor: 'upt',
            loading: false,
            currItemSitemap: {},
            showSitemap: false,
            preOrders: [],
            valid: false,
            dialog: false,
            SiteDialog: false,
            missingDialog: false,

            tankChartDialog: false,
            stationChartDialog: false,
            header: null,
            header2: null,
            region: null,
            regions: [],
            shiftSelected: '',
            shift_headers: [],
            readings: [],
            missing: [],
            count: 0,
            timer: null,
            headers: [
                {
                    text: 'Details',
                    value: 'data-table-expand',
                    sortable: false,
                    width: '2%'
                },
                { text: 'Station Number', value: 'StationNumber', width: '8%' },
                { text: 'Station', value: 'Name', width: '10%' },
                {
                    text: 'Products',
                    value: 'tank-data',
                    sortable: false,
                    width: '30%'
                },
                { text: 'Runout', value: 'closest_runout', width: '10%' },
                { text: 'Ongoing Order', value: 'current_order' },
                {
                    text: 'Actions',
                    value: 'actions',
                    sortable: false,
                    width: '10%'
                }
            ],
            oldHeaders: [
                { text: 'RRO Hrs', value: 'ROHrs', width: '5%' },
                { text: 'Site #', value: 'SiteNum', width: '7%' },
                { text: 'Station', value: 'Station', width: '7%' },
                { text: 'Product', value: 'Product', width: '6%' },
                { text: 'Volume', value: 'Volume', width: '7%' },
                { text: 'Ullage', value: 'Ullage', width: '7%' },
                { text: 'Avg Daily', value: 'AvgDaily', width: '4%' },
                { text: 'Avg Hourly', value: 'AvgHourly', width: '4%' },
                { text: 'ROHrs', value: 'ROHrs2', width: '5%' },
                { text: 'Reading Dttm', value: 'ReadingTime', width: '10%' },
                { text: 'Date RO', value: 'DateROHrs', width: '10%' },
                { text: 'Pend Qty', value: 'DispatchedQuantity', width: '4%' },
                { text: 'Pend Shift', value: 'Shift', width: '13%' },
                { text: 'Order', value: 'OrderID', width: '1%' },
                { text: 'L1', value: 'LoadNum', width: '1%' },
                { text: 'L2', value: 'LoadNumOrd', width: '1%' },
                { text: 'Driver', value: 'Name', width: '20%' },
                { text: 'Check', value: 'Check', width: '3%' },
                {
                    text: 'View',
                    value: 'show',
                    sortable: false,
                    width: '3%'
                },
                {
                    text: 'New',
                    value: 'actions',
                    sortable: false,
                    width: '3%'
                }
            ],
            SiteData: [],
            search: '',
            dataLoading: false,
            pendingDataLoading: false,
            totalReadingStations: 0,
            options: {},
            readings: [],
            stations: [],
            expanded: [],
            products: [],
            viewMode: 'tanks',
            groupBy: 'products',
            selectedItem: {},
            showReportHeader: false,
            contextOptions: [
                { name: 'Option 1', slug: 'option-1' },
                { name: 'Option 2', slug: 'option-2' },
                { type: 'divider' },
                { name: 'Option 3', slug: 'option-3' }
            ]
        };
    },
    created() {
        this.moment = moment;
    },
    mounted() {
        this.loadRegions();
        this.getReadings();
        this.getProducts();
        this.getStationNames();
    },
    computed: {},
    methods: {
        toggleGrouping() {
            this.groupBy = this.groupBy === 'tanks' ? 'products' : 'tanks';
        },
        async getReadings() {
            this.$store.state.isLoading = true;
            this.dataLoading = true;
            try {
                const { sortBy, sortDesc, page, itemsPerPage } = this.options;

                const sortOrder = sortDesc[0] ? 'desc' : 'asc';
                const sortField = sortBy[0] || 'StationID';

                const response = await axios.get(
                    `v2/system/readings/paginated`,
                    {
                        params: {
                            sortBy: sortField,
                            sortOrder: sortOrder,
                            page: page,
                            itemsPerPage: itemsPerPage,
                            search: this.search,
                            region: this.region
                        }
                    }
                );
                this.readings = response.data.data;
                this.totalReadingStations = response.data.total;
            } catch (error) {
                console.error('Error fetching readings:', error);
            } finally {
                this.dataLoading = false;
                this.$store.state.isLoading = false;
            }
        },

        async getProducts() {
            this.$store.state.isLoading = true;
            try {
                const response = await axios.get('product/active');
                this.products = response.data.data;
            } catch (error) {
                console.log(error);
            } finally {
                this.$store.state.isLoading = false;
            }
        },
        async getStationNames() {
            try {
                const response = await axios.get(
                    'v2/locations/stations/names/true'
                );
                this.stations = response.data.map(item => item.Name);
            } catch (error) {
                console.log(error);
            }
        },
        openSitemap: function(item) {
            let baseURL =
                'https://s3.amazonaws.com/upt-prod-s3-eleos/SitemapsAndDipcharts/';
            let sitemapLink =
                baseURL +
                item.StationID +
                '/Sitemap-' +
                item.StationID +
                '.pdf';
            item.SitemapLink = sitemapLink;
            this.currItemSitemap = item;
            this.showSitemap = true;
            console.log(item);
        },
        loadShiftHeaders: function() {
            this.$store.state.isLoading = true;
            axios
                .get('shift-header/dates/15')
                .then(response => {
                    this.shift_headers = response.data.data;
                    // console.log(this.shift_headers)
                })
                .catch(function(error) {
                    console.log(error);
                });
            this.$store.state.isLoading = false;
        },
        loadRegions: function() {
            this.$store.state.isLoading = true;
            axios
                .get('regions')
                .then(response => {
                    this.regions = response.data;
                    this.regions.push({
                        PrincipalRegionID: 5,
                        PrincipalRegion: 'All'
                    });
                    this.region = 5;
                })
                .catch(function(error) {
                    console.log(error);
                });
            this.$store.state.isLoading = false;
        },
        loadReadings() {
            this.$store.state.isLoading = true;
            this.dataLoading = true;
            axios
                .get('v2/system/readings/paginated')
                .then(response => {
                    this.readings = response.data;
                    console.log(this.readings);
                    const estTime = moment(
                        new Date().toLocaleString('en-US', {
                            timeZone: 'America/Toronto'
                        })
                    ).format('YYYY-MM-DD HH:mm:ss');
                    for (let i = 0; i < this.readings.length; i++) {
                        var readingDttm;
                        if (this.readings[i]['DateROHrs'] === null) {
                            readingDttm = moment(
                                this.readings[i]['ReadingTime']
                            ).format('YYYY-MM-DD HH:mm:ss');
                        } else {
                            readingDttm = moment(
                                this.readings[i]['DateROHrs']
                            ).format('YYYY-MM-DD HH:mm:ss');
                        }
                        this.readings[i]['Check'] = 0;
                        const realTime = moment(readingDttm).add(
                            this.readings[i]['ROHrs'],
                            'hours'
                        );
                        const fechaInicio = moment(
                            realTime,
                            'YYYY-MM-DD HH:mm:ss'
                        );
                        const fechaFin = moment(estTime, 'YYYY-MM-DD HH:mm:ss');
                        const diferenciaEnHoras = fechaInicio.diff(
                            fechaFin,
                            'hours'
                        );
                        this.readings[i]['ROHrs2'] = this.readings[i]['ROHrs'];
                        this.readings[i]['ROHrs'] = diferenciaEnHoras;
                    }
                    console.log(this.readings);
                    this.dataLoading = false;
                })
                .catch(function(error) {
                    console.log(error);
                });
            this.$store.state.isLoading = false;
        },
        onlyDate: function() {
            axios
                .get(
                    '/ftp-readings/pending-orders/all/n/' +
                        this.header.ShiftId +
                        '/' +
                        this.header2.ShiftId
                )
                .then(response => {
                    this.readings = response.data;
                    console.log(this.readings);
                    this.dataLoading = false;
                })
                .catch(function(error) {
                    console.log(error);
                });
        },
        filterDate: function() {
            axios
                .get(
                    '/ftp-readings/reads/region/pending/' +
                        this.region.PrincipalRegionID +
                        '/' +
                        this.header.ShiftId +
                        '/' +
                        this.header2.ShiftId
                )
                .then(response => {
                    this.readings = response.data;
                    this.dataLoading = false;
                })
                .catch(function(error) {
                    console.log(error);
                });
        },
        onlyFilter: function() {
            console.log(
                '/ftp-readings/reads/region/' + this.region.PrincipalRegionID
            );
            axios
                .get(
                    '/ftp-readings/reads/region/' +
                        this.region.PrincipalRegionID
                )
                .then(async response => {
                    this.readings = response.data;
                    const estTime = moment(
                        new Date().toLocaleString('en-US', {
                            timeZone: 'America/Toronto'
                        })
                    ).format('YYYY-MM-DD HH:mm:ss');
                    for (let i = 0; i < this.readings.length; i++) {
                        var readingDttm;
                        if (this.readings[i]['DateROHrs'] === null) {
                            readingDttm = moment(
                                this.readings[i]['ReadingTime']
                            ).format('YYYY-MM-DD HH:mm:ss');
                        } else {
                            readingDttm = moment(
                                this.readings[i]['DateROHrs']
                            ).format('YYYY-MM-DD HH:mm:ss');
                        }
                        const realTime = moment(readingDttm).add(
                            this.readings[i]['ROHrs'],
                            'hours'
                        );
                        const fechaInicio = moment(
                            realTime,
                            'YYYY-MM-DD HH:mm:ss'
                        );
                        const fechaFin = moment(estTime, 'YYYY-MM-DD HH:mm:ss');
                        const diferenciaEnHoras = fechaInicio.diff(
                            fechaFin,
                            'hours'
                        );

                        this.readings[i]['ROHrs2'] = this.readings[i]['ROHrs'];
                        this.readings[i]['ROHrs'] = diferenciaEnHoras;
                    }
                    this.dataLoading = false;
                })
                .catch(function(error) {
                    console.log(error);
                });
        },
        recall: function() {
            axios
                .get(
                    '/ftp-readings/pending-orders/all/n/' +
                        this.header.ShiftId +
                        '/' +
                        this.header2.ShiftId
                )
                .then(response => {
                    this.readings = response.data;
                    this.dataLoading = false;
                })
                .catch(function(error) {
                    console.log(error);
                });
        },
        refreshPending: function() {
            this.$store.state.isLoading = true;
            console.log(this.header);
            console.log(this.header2);
            this.dataLoading = true;
            /*//ejecuta la funcion cuando  solo las fechas estan activas
            if(this.header != null && this.header2 != null && (this.region == null || this.region == "") ){
                this.onlyDate();

            }*/
            //ejecuta la funcion cuando las fechas y region estan activos
            if (
                this.region != null &&
                this.region != '' &&
                this.header != null &&
                this.header2 != null
            ) {
                // Se verifica que no este seleccionado el parametro aLl en region ya que su id es  5
                if (this.region.PrincipalRegionID == 5) {
                    console.log(1);
                    this.onlyDate();
                } else {
                    console.log(2);
                    this.filterDate();
                }
            }
            //ejecuta la funcion cuando solo la region esta activa
            else if (this.region != null && this.region != '') {
                if (this.region.PrincipalRegionID != 5) {
                    console.log(3);
                    this.onlyFilter();
                } else {
                    console.log(4);
                    this.loadReadings();
                }
            }
            //ejecuta la funcion si ninguna de las anteriores se cumple
            else {
                this.recall;
            }
            this.$store.state.isLoading = false;
        },
        refreshTempPendingOrders: function() {
            this.$store.state.isLoading = true;

            var str = this.$store;
            str.dispatch('setSnackbar', {
                color: 'error',
                text:
                    'Sent update readings request, this process takes a few minutes.'
            });

            axios
                .get('/ftp-readings/update')
                .then(response => {
                    this.readings = response.data;
                    str.dispatch('setSnackbar', {
                        color: 'success',
                        text: 'Readings Update Complete, Refresh page'
                    });
                })
                .catch(function(error) {
                    console.log(error);
                });
            this.$store.state.isLoading = false;
        },
        createOrder: function(item) {
            console.log('item', item);
            this.$store.state.isLoading = true;
            this.SiteData = [];
            this.SiteData = item;
            this.$store.state.editedOrder.id = null;
            this.$store.state.siteReadings.OrderID = null;
            this.$store.state.siteReadings.SiteNum = '';
            this.$store.state.siteReadings.SiteNum = item.SiteNum;
            // this.$store.state.editedOrder.id = null;
            // this.$store.state.editedOrder.StationID = item.StationID;
            // this.$store.state.editedOrder.StationName = item.StationName;

            this.$store.state.order.rdnAdd = true;
            item = [];
            this.$store.state.isLoading = false;
        },
        siteCheck: function(value) {
            console.log(value);
            this.shiftSelected = value.StationNumber;
            this.$store.state.siteReadings.SiteNum = value.StationNumber;
            this.SiteDialog = true;
        },
        getProductCode: async function(st, ProductID) {
            let params = {
                Customer: st.CustomerId,
                Terminal: st.TerminalId,
                Supplier: st.SupplierId,
                Product: ProductID
            };
            let productCode = null;
            await axios
                .post('get-product-code-number', params)
                .then(response => {
                    console.log('productCode: ', response.data);
                    productCode = response.data;
                })
                .catch(function(error) {
                    console.log(error);
                });

            return productCode;
        },
        createOrderDetails: async function(OrderID, st) {
            console.log('createOrderDetails for: ', OrderID, ' from ', st);
            var str = this.$store;

            for (let prd of st.OrderDetails) {
                let prdCode = await this.getProductCode(st, prd.ProductID);
                console.log(prdCode);
                let params = {
                    OrderID: OrderID,
                    ProductType: prd.ProductID,
                    ProductCode: prdCode,
                    ProductQuantity: 0,
                    DispatchedQuantity: 0
                };

                await axios
                    .post('order-detail', params)
                    .then(response => {
                        console.log(response.data);
                    })
                    .catch(function(error) {
                        console.log(error);
                        str.dispatch('setSnackbar', {
                            color: 'error',
                            text:
                                'Something is wrong. Please contact the administrator.'
                        });
                    });
            }
        },
        createOrders: function() {
            var str = this.$store;
            console.log('createOrders');
            console.log(this.preOrders);

            var currDate = new Date();
            var year = currDate.getFullYear();
            var month = currDate.getMonth() + 1;
            var day = currDate.getDate();
            var formattedDate =
                year +
                '/' +
                (month < 10 ? '0' : '') +
                month +
                '/' +
                (day < 10 ? '0' : '') +
                day;
            console.log(formattedDate);

            for (let st of this.preOrders) {
                let isSpecific = 0;
                for (const prd of st.OrderDetails) {
                    if (prd.SpecificTrailerOnly == 1) {
                        isSpecific = prd.ProductID;
                    }
                }
                console.log(isSpecific);

                var params = {
                    Station: st.StationId,
                    Customer: st.CustomerId,
                    Terminal: st.TerminalId,
                    Supplier: st.SupplierId,
                    DateRequested: formattedDate,
                    //TimeOrdered: this.editedItem.TimeOrdered,
                    DateOrdered: formattedDate,
                    LoadingNumber: st.LoadingNumber,
                    //SpecialInstructions: this.editedItem.SpecialInstructions,
                    //DispatchComments: this.editedItem.DispatchComments,
                    //SplitTypeID: this.editedItem.SplitType.SplitTypeID,
                    //NoLoadTypeID: this.editedItem.NoLoadType.NoLoadTypeID,
                    //OrderTypeID: this.editedItem.OrderType.OrderTypeID,
                    ReadyForDispatch: 1,
                    //RTHrs: SiteRTHrs,
                    //ROHrsTime: this.editedItem.ROHrsTime,
                    //RetainHrs: this.editedItem.RetainHrs,
                    isSpecific: isSpecific,
                    //Validate: this.editedItem.Validate,
                    //ROTime: moment(ROTime).format('YYYY-MM-DD HH:mm:ss'),
                    //RTTime: moment(RTTime).format('YYYY-MM-DD HH:mm:ss')
                    AutomaticCreation: 1
                };

                axios
                    .post('order', params)
                    .then(async response => {
                        console.log(response.data);
                        //this.updateLastOrder(st.StationId, response.data.OrderID);
                        await this.createOrderDetails(
                            response.data.OrderID,
                            st
                        );
                    })
                    .catch(function(error) {
                        str.state.isLoading = false;
                        console.log(error);
                        str.dispatch('setSnackbar', {
                            color: 'error',
                            text:
                                'Something is wrong. Please contact the administrator.'
                        });
                    });
            }

            str.state.isLoading = false;
            str.dispatch('setSnackbar', {
                color: 'success',
                text: 'Orders created successfully.'
            });
        },
        updateLastOrder: function(stationID, OrderID) {
            let params = {
                StationID: stationID,
                OrderID: OrderID
            };

            axios
                .put('ftp-readings/update/last-order', params)
                .then(response => {
                    console.log(response.data);
                })
                .catch(function(error) {
                    console.log(error);
                });
        },
        multiOrders: async function() {
            console.log('Hola');
            var str = this.$store;
            str.state.isLoading = true;
            var stations = this.readings
                .filter(x => {
                    return x.Check == true;
                })
                .map(x => {
                    return {
                        Station: x.StationID,
                        SiteNum: x.SiteNum
                    };
                });
            console.log(stations);
            var parms = {
                Stations: this.eliminarDuplicados(stations)
            };
            await axios
                .post('order/multi/retain/orders', parms)
                .then(response => {
                    console.log(response.data);
                    this.preOrders = response.data;
                    //this.$store.state.readings.multiple = true;
                    //str.state.isLoading = false;
                    this.createOrders();
                })
                .catch(function(error) {
                    str.state.isLoading = false;
                    console.log(error);
                });

            //console.log(this.preOrders);
        },
        eliminarDuplicados: function(arr) {
            const uniqueElements = new Set();
            return arr.filter(item => {
                const serializedItem = JSON.stringify(item);
                if (!uniqueElements.has(serializedItem)) {
                    uniqueElements.add(serializedItem);
                    return true;
                }
                return false;
            });
        },
        validate(i) {
            if (this.$refs.form.validate() == false) {
                this.$refs.form.validate();
            } else {
                switch (i) {
                    case 0:
                        this.refreshPending();
                        break;

                    case 1:
                        this.printSafeFillPdf();
                        break;

                    case 2:
                        this.printRunoutPdf();
                        break;

                    case 3:
                        this.printRunoutReport(true);
                        break;

                    case 4:
                        this.printSafeFillReport(true);
                        break;
                    case 5:
                        this.printRetainPdf(true);
                        break;
                }
            }
        },
        openTankChart(tankOrProduct) {
            if (this.groupBy === 'tanks') {
                this.selectedItem = tankOrProduct;
            } else {
                // For product view, you might want to show a chart for all tanks of this product
                // or select the first tank. Adjust this logic based on your requirements.
                const firstTank = this.getProductTanks(
                    this.selectedStation,
                    tankOrProduct.product_id
                )[0];
                this.selectedItem = firstTank;
            }
            this.tankChartDialog = true;
        },
        openStationChart(item) {
            this.selectedItem = item;
            this.stationChartDialog = true;
        },
        toggleViewMode() {
            this.viewMode = this.viewMode === 'tanks' ? 'products' : 'tanks';
        },

        getGroupedData(item) {
            if (!item) return [];
            if (this.groupBy === 'tanks') {
                return item.tanks || [];
            } else {
                return Object.values(item.products || {});
            }
        },

        getProductTanks(item, productId) {
            return (item.tanks || []).filter(
                tank => tank.Product === productId
            );
        },

        groupTanksByProduct(tanks) {
            const groupedTanks = {};
            tanks.forEach(tank => {
                if (!groupedTanks[tank.Product]) {
                    groupedTanks[tank.Product] = {
                        Product: tank.Product,
                        productName: this.getProductName(tank.Product),
                        tanks: []
                    };
                }
                groupedTanks[tank.Product].tanks.push(tank);
            });
            return Object.values(groupedTanks);
        },

        getTotalVolume(item, tankOrProduct) {
            if (this.groupBy === 'tanks') {
                return tankOrProduct.volume || 0;
            } else {
                return tankOrProduct.total_volume || 0;
            }
        },

        getTotalSafePump(item, tankOrProduct) {
            if (this.groupBy === 'tanks') {
                return tankOrProduct.SafePump || 0;
            } else {
                return tankOrProduct.total_safe_pump || 0;
            }
        },

        getTotalSafeFill(item, tankOrProduct) {
            if (this.groupBy === 'tanks') {
                return tankOrProduct.SafeFill || 0;
            } else {
                return tankOrProduct.total_safe_fill || 0;
            }
        },

        getPercentage(item, tankOrProduct) {
            const volume = this.getTotalVolume(item, tankOrProduct);
            const safePump = this.getTotalSafePump(item, tankOrProduct);
            const safeFill = this.getTotalSafeFill(item, tankOrProduct);

            if (!safePump || !safeFill) return 0;

            return Math.min(
                100,
                Math.max(0, ((volume - safePump) / (safeFill - safePump)) * 100)
            ).toFixed(2);
        },
        getTanks(tankOrProduct) {
            return this.groupBy === 'tanks'
                ? [tankOrProduct]
                : tankOrProduct.tanks;
        },
        //Utils
        parseTimestamp(timestamp) {
            return moment(timestamp).format('YYYY-MM-DD HH:mm:ss');
        },
        isLessThan24Hours(datetime) {
            if (!datetime) return false;
            // If the difference between the current time and the datetime is less than 24 hours
            return (
                moment().diff(
                    moment(datetime, 'YYYY-MM-DD HH:mm:ss'),
                    'hours'
                ) < 24
            );
        },

        getProductName(ProductID) {
            const product = this.products.find(p => p.ProductID === ProductID);
            return product?.Product || 'Unknown';
        },
        runoutCountdown(runoutDate) {
            if (!runoutDate) return 'N/A';
            const runout = moment(runoutDate);
            const now = this.getCurrentTorontoTime();
            let diff = runout.diff(now, 'minutes');

            if (diff <= 0) return 'Runout reached!';

            const days = Math.floor(diff / (24 * 60));
            diff %= 24 * 60;
            const hours = Math.floor(diff / 60);
            const minutes = diff % 60;

            let output = '';
            if (days > 0) output += `${days} day${days !== 1 ? 's' : ''} `;
            if (hours > 0) output += `${hours} hr${hours !== 1 ? 's' : ''}. `;
            if (minutes > 0 || (days === 0 && hours === 0))
                output += `and ${minutes} min.`;

            return output.trim();
        },
        timeSince(date) {
            let now = moment().tz('America/Toronto');
            let then = moment(date);
            let diff = now.diff(then, 'minutes');
            if (diff < 60) return `${diff}m`;
            diff = now.diff(then, 'hours');
            if (diff < 24) return `${diff}h`;
            diff = now.diff(then, 'days');
            return `${diff}d`;
        },
        //CSS
        calculateFillPercentage(item, tankOrProduct) {
            const volume = this.getTotalVolume(item, tankOrProduct);
            const safeFill = this.getTotalSafeFill(item, tankOrProduct);
            return Math.min(100, Math.max(0, (volume / (safeFill || 1)) * 100));
        },

        calculateRunoutColor(item, tankOrProduct) {
            if (this.groupBy === 'products') {
                return this.getColorForRunoutDate(
                    moment(tankOrProduct.safe_runout_at)
                );
            } else {
                if (
                    !tankOrProduct.safe_runout_at ||
                    !moment(tankOrProduct.safe_runout_at).isValid()
                ) {
                    return this.calculateFillColor(item, tankOrProduct);
                }
                return this.getColorForRunoutDate(
                    moment(tankOrProduct.safe_runout_at)
                );
            }
        },

        calculateFillColor(item, tankOrProduct) {
            const percentage = this.getPercentage(item, tankOrProduct);
            if (percentage <= 0) {
                return 'rgb(255, 255, 255)'; // White for empty
            } else if (percentage <= 10) {
                return 'rgb(255, 204, 203)'; // Light red for 10% or less
            } else if (percentage <= 25) {
                // Gradient from light red to light orange for 10-25%
                const percentageInRange = (percentage - 10) / 15;
                const r = 255;
                const g = Math.floor(204 + (255 - 204) * percentageInRange);
                const b = Math.floor(203 + (196 - 203) * percentageInRange);
                return `rgb(${r}, ${g}, ${b})`;
            } else if (percentage <= 50) {
                // Gradient from light orange to light yellow for 25-50%
                const percentageInRange = (percentage - 25) / 25;
                const r = 255;
                const g = Math.floor(255 + (255 - 255) * percentageInRange);
                const b = Math.floor(196 + (224 - 196) * percentageInRange);
                return `rgb(${r}, ${g}, ${b})`;
            } else if (percentage <= 75) {
                // Gradient from light yellow to light green for 50-75%
                const percentageInRange = (percentage - 50) / 25;
                const r = Math.floor(255 - (255 - 144) * percentageInRange);
                const g = 255;
                const b = Math.floor(224 + (144 - 224) * percentageInRange);
                return `rgb(${r}, ${g}, ${b})`;
            } else {
                return 'rgb(144, 238, 144)'; // Light green for more than 75%
            }
        },

        getColorForRunoutDate(runoutDate) {
            const now = this.getCurrentTorontoTime();
            const hoursUntilRunout = runoutDate.diff(now, 'hours');

            if (hoursUntilRunout <= 24) {
                return 'rgb(255, 204, 203)'; // Light red for 24 hours or less
            } else if (hoursUntilRunout <= 48) {
                // Gradient from light red to light orange for 24-48 hours
                const percentage = (hoursUntilRunout - 24) / 24;
                const r = 255;
                const g = Math.floor(204 + (228 - 204) * percentage);
                const b = Math.floor(203 + (196 - 203) * percentage);
                return `rgb(${r}, ${g}, ${b})`;
            } else if (hoursUntilRunout <= 96) {
                // Gradient from light orange to light yellow for 48-96 hours
                const percentage = (hoursUntilRunout - 48) / 48;
                const r = 255;
                const g = Math.floor(228 + (255 - 228) * percentage);
                const b = Math.floor(196 + (224 - 196) * percentage);
                return `rgb(${r}, ${g}, ${b})`;
            } else if (hoursUntilRunout <= 192) {
                // Gradient from light yellow to light green for 96-192 hours (4-8 days)
                const percentage = (hoursUntilRunout - 96) / 96;
                const r = Math.floor(255 - (255 - 144) * percentage);
                const g = 238;
                const b = Math.floor(224 - (224 - 144) * percentage);
                return `rgb(${r}, ${g}, ${b})`;
            } else {
                return 'rgb(144, 238, 144)'; // Light green for more than 8 days
            }
        },
        getRunoutColor(closestRunout) {
            const now = this.getCurrentTorontoTime();
            const runoutDate = moment.tz(closestRunout, 'America/Toronto');
            const hoursUntilRunout = runoutDate.diff(now, 'hours');

            if (hoursUntilRunout <= 0) {
                return 'rgb(255, 0, 0)'; // Red for runout reached
            } else if (hoursUntilRunout <= 24) {
                return 'rgb(255, 99, 71)'; // Tomato red for 12 hours or less
            } else if (hoursUntilRunout <= 36) {
                return 'rgb(255, 165, 0)'; // Orange for 12-24 hours
            } else if (hoursUntilRunout <= 48) {
                return 'rgb(255, 255, 224)'; // Light yellow for 24-48 hours
            } else {
                return 'rgb(144, 238, 144)'; // Light green for more than 48 hours
            }
        },

        formatRunoutTime(closestRunout) {
            return moment(closestRunout).format('YYYY-MM-DD HH:mm:ss');
        },
        getCurrentTorontoTime() {
            return moment().tz('America/Toronto');
        }
    }
};
</script>

<style scoped>
.custom-chip {
    position: relative;
    display: inline-flex;
    align-items: center;
    min-width: 100px; /* Fixed width */
    height: 24px;
    border-radius: 4px;
    padding: 0 8px;
    background-color: #e0e0e0; /* Base gray color */
    overflow: hidden;
}

.chip-fill {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    transition: width 0.3s ease, background-color 0.3s ease;
}

.chip-text {
    position: relative;
    color: #000; /* Text color */
    font-size: 12px;
    width: 100%;
    text-align: center;
    font-weight: bold;
}
</style>
